import Autocomplete from '@mui/material/Autocomplete';
import { SxProps, Theme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import { Ref, ReactNode } from 'react';
import { InputText } from './InputText';

export type TimeOption = {
  label: string;
  minutesFromMidnight: number;
};

const TIME_OPTIONS: TimeOption[] = Array.from({ length: 24 * 4 }, (_, index) => {
  const minutesFromMidnight = index * 15;
  const hours24 = Math.floor(minutesFromMidnight / 60);
  const minutes = minutesFromMidnight % 60;
  const period = hours24 >= 12 ? 'pm' : 'am';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const label = `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;

  return { label, minutesFromMidnight };
});

const normalizeQuery = (value: string) =>
  value.toLowerCase().replace(/\s+/g, '').replace(/\./g, '');

/** Match typed prefixes like "7", "7:1", "7:15a", "715pm", "7am". */
export const filterTimeOptions = (options: TimeOption[], inputValue: string): TimeOption[] => {
  const query = normalizeQuery(inputValue);
  if (!query) {
    return options;
  }

  return options.filter((option) => {
    const label = normalizeQuery(option.label);

    // Progressive label match for strings like "7:0" / "7:00a"
    if (/[:ap]/i.test(query) && label.startsWith(query)) {
      return true;
    }

    // "715am" / "715" (no colon) or "7:15am" / "7a" / "7"
    const match = query.match(/^(\d{1,2})(?::(\d{0,2})|(\d{2}))?(a|p|am|pm)?$/);
    if (!match) {
      return false;
    }

    const hour = Number(match[1]);
    const typedMinutes = match[2] ?? match[3] ?? '';
    const periodToken = match[4];
    const optionHour12 = Math.floor(option.minutesFromMidnight / 60) % 12 || 12;
    const optionMinutes = (option.minutesFromMidnight % 60).toString().padStart(2, '0');
    const optionPeriod = option.minutesFromMidnight >= 12 * 60 ? 'pm' : 'am';

    if (hour !== optionHour12) {
      return false;
    }
    if (typedMinutes && !optionMinutes.startsWith(typedMinutes)) {
      return false;
    }
    if (periodToken) {
      const period = periodToken.startsWith('a') ? 'am' : 'pm';
      if (optionPeriod !== period) {
        return false;
      }
    }

    return true;
  });
};

const minutesFromMidnightOf = (value: Dayjs | Date | null | undefined): number | null => {
  if (!value) {
    return null;
  }

  const time = dayjs(value);
  if (!time.isValid()) {
    return null;
  }

  return time.hour() * 60 + time.minute();
};

const roundToNearestOption = (
  value: Dayjs | Date | null | undefined,
  options: TimeOption[] = TIME_OPTIONS
): TimeOption | null => {
  if (!value) {
    return null;
  }

  const time = dayjs(value);
  if (!time.isValid()) {
    return null;
  }

  const totalMinutes = time.hour() * 60 + time.minute();
  const rounded = Math.round(totalMinutes / 15) * 15;
  const clamped = ((rounded % (24 * 60)) + 24 * 60) % (24 * 60);
  return options.find((option) => option.minutesFromMidnight === clamped) ?? null;
};

const optionToDate = (option: TimeOption, reference?: Dayjs | Date | null): Date => {
  const base = reference && dayjs(reference).isValid() ? dayjs(reference) : dayjs();
  const hours24 = Math.floor(option.minutesFromMidnight / 60);
  const minutes = option.minutesFromMidnight % 60;

  return base.hour(hours24).minute(minutes).second(0).millisecond(0).toDate();
};

type CustomTimePickerProps = {
  label?: string;
  value?: Dayjs | Date | null;
  onChange?: (value: Date | null) => void;
  onAccept?: (value: Date | null) => void;
  inputRef?: Ref<any>;
  sx?: SxProps<Theme>;
  orientation?: string;
  /** Earliest selectable time (inclusive). Options before this are hidden. */
  minTime?: Dayjs | Date | null;
  /** Overrides minTime using minutes from midnight (0–1440). 1440 hides every option. */
  minMinutesFromMidnight?: number | null;
  slotProps?: {
    textField?: {
      helperText?: ReactNode;
      error?: boolean;
    };
  };
};

export const CustomTimePicker = ({
  label,
  value,
  onChange,
  onAccept,
  inputRef,
  sx,
  minTime,
  minMinutesFromMidnight,
  slotProps,
}: CustomTimePickerProps) => {
  const minMinutesRaw =
    minMinutesFromMidnight == null ? minutesFromMidnightOf(minTime) : minMinutesFromMidnight;
  const minMinutes =
    minMinutesRaw == null ? null : Math.ceil(minMinutesRaw / 15) * 15;
  const options =
    minMinutes == null
      ? TIME_OPTIONS
      : TIME_OPTIONS.filter((option) => option.minutesFromMidnight >= minMinutes);

  const selected = roundToNearestOption(value ?? null, options);
  const helperText = slotProps?.textField?.helperText;
  const hasError = Boolean(slotProps?.textField?.error || helperText);

  return (
    <Autocomplete
      options={options}
      value={selected}
      sx={{ flex: 1, minWidth: 0, ...((sx as object) || {}) }}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) =>
        option.minutesFromMidnight === current.minutesFromMidnight
      }
      filterOptions={(available, state) => filterTimeOptions(available, state.inputValue)}
      onChange={(_event, option) => {
        const next = option ? optionToDate(option, value) : null;
        onChange?.(next);
        onAccept?.(next);
      }}
      renderInput={(params) => (
        <InputText
          {...params}
          label={label}
          error={hasError}
          helperText={helperText || ''}
          inputRef={inputRef}
          placeholder="e.g. 7:00am"
          fullWidth
        />
      )}
    />
  );
};

export { TIME_OPTIONS };
